import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LytxWindow } from '@lytx/platform.ui.components';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, RowSelectedEvent } from 'ag-grid-community';
import { ToastrService } from 'ngx-toastr';
import { DeviceMonitoringModalLinkComponent } from 'src/app/monitoring/device-monitoring-modal-link/device-monitoring-modal-link.component';
import {
  DeviceBatteryQueryRequest,
  DeviceBatteryService,
  IDeviceBatteryRecord,
} from 'src/app/shared/services/device-battery/device-battery.service';
import { ShadowPageService } from '../shadow-page.service';

// Configurable battery-health and battery-type options. These are static for
// now (matching the Figma); if/when more values are added on the backend we
// will swap these for an API-driven lookup.
// "Unknown" is intentionally absent — it's a placeholder the backend returns
// for devices without battery telemetry, not a real health state, so we don't
// expose it as a selectable filter. Rows carrying it are also hidden from the
// grid in refresh().
const BATTERY_HEALTH_FILTERS: { label: string; value: string }[] = [
  { label: 'All', value: '' },
  { label: 'Normal (Good)', value: 'Good' },
  { label: 'Weak', value: 'Weak' },
  { label: 'Faulty (Critical)', value: 'Critical' },
  { label: 'Not Charging', value: 'Not Charging' },
];

const PLACEHOLDER_BATTERY_HEALTH = 'Unknown';

// Inline styles let the dot survive ag-grid's cellRenderer pipeline, which
// strips the component's emulated view-encapsulation attributes. Unknown is
// intentionally absent so its row renders as plain text in the default color.
const BATTERY_HEALTH_DOT_COLORS: Record<string, string> = {
  Good: '#00B884',
  Weak: '#FFD600',
  Critical: '#F94E4E',
  'Not Charging': '#F94E4E',
};

// The battery-type options are seeded with "All" only; the rest are appended
// dynamically from unique batteryType values observed in API responses so the
// dropdown reflects every manufacturer the deviceshadow data actually returns.
const BATTERY_TYPE_OPTIONS_SEED: { label: string; value: string }[] = [
  { label: 'All', value: '' },
];

// Synthetic placeholder produced by DeviceBatteryService.toBatteryRecord when
// batteryIssueManufacturerName is missing. Selecting it must filter to records
// where the manufacturer field is absent, not an exact-match on the string.
const SYNTHETIC_OTHER_BATTERY_TYPE = 'Other';

const SEARCH_FIELDS: {
  label: string;
  value: keyof DeviceBatteryQueryRequest;
}[] = [
  { label: 'Serial Number', value: 'serialNumber' },
  { label: 'Company Name', value: 'companyName' },
  { label: 'Vehicle Name', value: 'vehicleName' },
  { label: 'VIN', value: 'vin' },
];

// Match the page size used by the shared ShadowPageBaseComponent so the
// simple-paginator behaves consistently across Insights tabs.
const PAGE_SIZE = 40;

// Used as the CSV filename suffix (consistent with the QueryOptions.name pattern
// shadow-page-base passes to ShadowPageService.getCsv).
const CSV_QUERY_NAME = 'Device Battery';

@Component({
  selector: 'dcc-device-battery-insights',
  templateUrl: './device-battery-insights.component.html',
  styleUrls: ['./device-battery-insights.component.scss'],
})
export class DeviceBatteryInsightsComponent implements OnInit, OnDestroy {
  @ViewChild(AgGridAngular) grid!: AgGridAngular;

  readonly batteryHealthOptions = BATTERY_HEALTH_FILTERS;
  batteryTypeOptions: { label: string; value: string }[] = [
    ...BATTERY_TYPE_OPTIONS_SEED,
  ];
  readonly searchFieldOptions = SEARCH_FIELDS;

  // Filter values shown in the top row. batteryType / batteryHealth use null
  // as the "nothing picked yet" sentinel so the disabled placeholder option is
  // shown by default; selecting "All" explicitly sets them to ''.
  companyId = '';
  batteryType: string | null = null;
  batteryHealth: string | null = null;
  searchField: keyof DeviceBatteryQueryRequest = SEARCH_FIELDS[0].value;
  searchValue = '';

  // Paging / table state.
  pageSize = PAGE_SIZE;
  pageNumber = 1;
  totalCount = 0;
  records: IDeviceBatteryRecord[] = [];
  selectedSerialNumbers: string[] = [];
  isLoading = false;

  private scrollId: string | undefined;

  readonly defaultColumnConfiguration: ColDef = {
    sortable: false,
    resizable: true,
    suppressMenu: true,
  };

  readonly columnDefinitions: ColDef[] = [
    {
      headerName: '',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 48,
      pinned: 'left',
    },
    {
      field: 'serialNumber',
      headerName: 'DEVICE',
      headerTooltip: 'Lytx Event Recorder Serial Number',
      cellRenderer: DeviceMonitoringModalLinkComponent,
    },
    {
      field: 'batteryType',
      headerName: 'BATTERY TYPE',
      headerTooltip: 'Battery manufacturer reported by the device',
    },
    {
      field: 'companyName',
      headerName: 'COMPANY',
      headerTooltip: 'Company Name',
    },
    {
      field: 'batteryHealthDisplay',
      headerName: 'BATTERY HEALTH',
      headerTooltip: 'Battery health reported by the device',
      cellRenderer: (params: { data?: IDeviceBatteryRecord }) =>
        this.renderBatteryHealth(params.data),
    },
    {
      field: 'chargeStatus',
      headerName: 'CHARGE STATUS',
      headerTooltip: 'Most recent charge status',
    },
    {
      field: 'avgVolt',
      headerName: 'AVG. VOLT',
      headerTooltip: 'Most recent battery voltage',
    },
    {
      field: 'idleHrs',
      headerName: 'IDLE HRS',
      headerTooltip:
        'Idle discharge time from device.batterybackupstats (hours)',
    },
    {
      field: 'highCycling',
      headerName: 'HIGH CYCLING',
      headerTooltip:
        'Whether the battery has shown frequent low-voltage recharge attempts',
    },
    {
      field: 'lastTelemetryRec',
      headerName: 'LAST TELEMETRY REC',
      headerTooltip: 'Timestamp of last battery telemetry record',
      valueFormatter: (params: { value?: string }) =>
        params.value ? new Date(params.value).toLocaleString() : '',
    },
    {
      field: 'action',
      headerName: 'ACTION',
      headerTooltip: 'Recommended action based on health and cycling',
      cellClass: (params: { value?: string }) =>
        params.value === 'Replace Device'
          ? 'device-battery__cell--action-replace'
          : 'device-battery__cell--action',
    },
  ];

  constructor(
    private readonly batteryService: DeviceBatteryService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly shadowPageService: ShadowPageService,
    private readonly toastr: ToastrService,
    private readonly window: LytxWindow,
  ) {}

  ngOnInit(): void {
    // Restore filter state from the URL so the user can navigate to a device
    // profile and come back to the same filtered grid.
    const params = this.route.snapshot.queryParams;
    this.companyId = params['companyId'] ?? '';
    this.batteryType = params['batteryType'] ?? null;
    this.batteryHealth = params['batteryHealth'] ?? null;
    const rawSearchField = params['searchField'];
    this.searchField = SEARCH_FIELDS.some((f) => f.value === rawSearchField)
      ? (rawSearchField as keyof DeviceBatteryQueryRequest)
      : SEARCH_FIELDS[0].value;
    this.searchValue = params['searchValue'] ?? '';
    this.refresh();
    // Pre-discover all battery types across every health bucket so the
    // Battery Type dropdown is independent of the active health filter.
    void this.loadAllBatteryTypes();
  }

  ngOnDestroy(): void {
    this.releaseScroll();
  }

  onFilterChange(): void {
    this.persistQueryParams();
    this.pageNumber = 1;
    this.releaseScroll();
    this.refresh();
  }

  onSearchClick(): void {
    this.onFilterChange();
  }

  onResetClick(): void {
    this.companyId = '';
    this.batteryType = null;
    this.batteryHealth = null;
    this.searchField = SEARCH_FIELDS[0].value;
    this.searchValue = '';
    this.onFilterChange();
  }

  onClearAll(): void {
    this.grid?.api?.deselectAll();
    this.selectedSerialNumbers = [];
  }

  onRowSelectionChanged(_event: RowSelectedEvent): void {
    const selectedRows: IDeviceBatteryRecord[] =
      this.grid?.api?.getSelectedRows() ?? [];
    this.selectedSerialNumbers = selectedRows.map((r) => r.serialNumber);
  }

  onModifySettings(): void {
    // Modify Settings targets a single device — the settings hierarchy view
    // displays one entity at a time. We deep-link to that entity's settings
    // (entity id === serial number for devices) so the page loads the right
    // device immediately.
    if (this.selectedSerialNumbers.length !== 1) {
      return;
    }
    const [serialNumber] = this.selectedSerialNumbers;
    this.openInNewTab(['/settings/hierarchy', serialNumber, 'current']);
  }

  onIssueCommands(): void {
    if (!this.selectedSerialNumbers.length) {
      return;
    }
    // /commands picks up these params in ngOnInit and auto-opens the Issue
    // New Command modal with the serials pre-filled.
    this.openInNewTab(['/commands'], {
      serialNumbers: this.selectedSerialNumbers.join(','),
      openCommandModal: true,
    });
  }

  onDownloadCsv(): void {
    // Defer to ShadowPageService so the device-battery tab uses the same
    // async/polling CSV flow as the other Insights tabs — backend renders the
    // full result set (not just the current page) and notifies the user via
    // toasts when the download is ready.
    this.shadowPageService.getCsv({
      ...this.buildRequest(),
      name: CSV_QUERY_NAME,
    });
  }

  onClickFirst(): void {
    this.pageNumber = 1;
    this.releaseScroll();
    this.refresh();
  }

  onPaginate(): void {
    if (this.pageNumber < this.pageCount) {
      this.pageNumber++;
      this.refresh();
    }
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  get selectedCount(): number {
    return this.selectedSerialNumbers.length;
  }

  // Both row actions open in a new tab so the user keeps this tab's filters,
  // scroll cursor and row selection intact while working the selected devices.
  // 'noopener' keeps the new tab from reaching back via window.opener.
  private openInNewTab(
    commands: (string | number)[],
    queryParams?: Record<string, unknown>,
  ): void {
    const urlTree = this.router.createUrlTree(
      commands,
      queryParams ? { queryParams } : {},
    );
    this.window.open(this.router.serializeUrl(urlTree), '_blank', 'noopener');
  }

  private async refresh(): Promise<void> {
    this.isLoading = true;
    try {
      const request = this.buildRequest();
      const response = await this.batteryService.getDeviceBatteries(request);
      // Hide placeholder-health rows; the backend returns "Unknown" for
      // devices without battery telemetry, which we don't want surfaced in
      // the Insights grid.
      const unknownCount = response.results.filter(
        (record) => record.batteryHealth === PLACEHOLDER_BATTERY_HEALTH,
      ).length;
      const visibleResults = response.results.filter(
        (record) => record.batteryHealth !== PLACEHOLDER_BATTERY_HEALTH,
      );
      this.records = visibleResults;
      // Subtract the Unknown records found on this page so the billboard /
      // CSV count reflects what the user can actually see. This is still an
      // approximation (Unknown records on other pages aren't accounted for)
      // but avoids the most egregious mismatch on page 1.
      this.totalCount = Math.max(0, response.total - unknownCount);
      this.scrollId = response.scrollId;
      this.mergeBatteryTypeOptions(visibleResults);
    } catch (error) {
      console.error('Failed to load device battery insights:', error);
      this.toastr.error('Failed to load device battery data.');
      this.records = [];
      this.totalCount = 0;
    } finally {
      this.isLoading = false;
      this.changeDetector.markForCheck();
    }
  }

  private mergeBatteryTypeOptions(results: IDeviceBatteryRecord[]): void {
    const seen = new Set(this.batteryTypeOptions.map((opt) => opt.value));
    const additions: { label: string; value: string }[] = [];
    for (const record of results) {
      const value = record.batteryType;
      if (!value || seen.has(value)) {
        continue;
      }
      seen.add(value);
      additions.push({ label: value, value });
    }
    if (additions.length) {
      this.batteryTypeOptions = [...this.batteryTypeOptions, ...additions];
    }
  }

  private async loadAllBatteryTypes(): Promise<void> {
    // Fetch a sample from every health bucket in parallel so the Battery Type
    // dropdown always reflects all possible manufacturers regardless of which
    // health filter the user has selected. Without this, rare types (e.g.
    // Expocell) only appear after the user visits the specific health state
    // that surfaces them, making the dropdown contents order-dependent.
    const healthBuckets = BATTERY_HEALTH_FILTERS.filter(
      (opt) => opt.value !== '',
    ).map((opt) => opt.value);
    const baseRequest: DeviceBatteryQueryRequest = {
      count: PAGE_SIZE,
      useScroll: false,
    };
    if (this.companyId) {
      const parsed = Number(this.companyId);
      if (Number.isFinite(parsed)) {
        baseRequest.companyId = parsed;
      }
    }
    const responses = await Promise.all(
      healthBuckets.map((health) =>
        this.batteryService
          .getDeviceBatteries({ ...baseRequest, batteryHealth: health })
          .catch(() => ({ results: [], total: 0, scrollId: undefined })),
      ),
    );
    const allRecords = responses.flatMap((r) => r.results);
    this.mergeBatteryTypeOptions(allRecords);
    this.changeDetector.markForCheck();
  }

  private buildRequest(): DeviceBatteryQueryRequest {
    const request: DeviceBatteryQueryRequest = {
      count: this.pageSize,
      // We re-issue the search from page 1 on filter changes, so the scroll
      // cursor only carries forward across explicit Next clicks.
      scrollId: this.pageNumber === 1 ? undefined : this.scrollId,
      useScroll: true,
    };

    if (this.companyId) {
      const parsed = Number(this.companyId);
      if (Number.isFinite(parsed)) {
        request.companyId = parsed;
      }
    }
    if (this.batteryType === SYNTHETIC_OTHER_BATTERY_TYPE) {
      (request as Record<string, unknown>)['doesNotExist'] = [
        'batteryIssueManufacturerName',
      ];
    } else if (this.batteryType) {
      request.batteryIssueManufacturerName = this.batteryType;
    }
    if (this.batteryHealth) {
      request.batteryHealth = this.batteryHealth;
    }
    if (this.searchValue) {
      // Explicit switch prevents CodeQL js/remote-property-injection: the
      // property key is a compile-time string literal in every branch, never
      // a runtime-variable derived from URL params.
      switch (this.searchField) {
        case 'serialNumber':
          request.serialNumber = this.searchValue;
          break;
        case 'companyName':
          request.companyName = this.searchValue;
          break;
        case 'vehicleName':
          request.vehicleName = this.searchValue;
          break;
        case 'vin':
          request.vin = this.searchValue;
          break;
      }
    }
    return request;
  }

  private persistQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        companyId: this.companyId || null,
        batteryType: this.batteryType || null,
        batteryHealth: this.batteryHealth || null,
        searchField: this.searchValue ? this.searchField : null,
        searchValue: this.searchValue || null,
      },
      queryParamsHandling: 'merge',
    });
  }

  private releaseScroll(): void {
    if (this.scrollId) {
      this.batteryService.deleteScrollId(this.scrollId).catch(() => undefined);
      this.scrollId = undefined;
    }
  }

  private renderBatteryHealth(data: IDeviceBatteryRecord | undefined): string {
    if (!data) {
      return '';
    }
    // The display value originates from the deviceShadow API and is injected
    // straight into ag-grid's innerHTML by the cell renderer, so escape any
    // markup-sensitive characters before concatenating.
    const display = escapeHtml(data.batteryHealthDisplay || '');
    const color = BATTERY_HEALTH_DOT_COLORS[data.batteryHealth];
    if (!color) {
      return display;
    }
    return (
      `<span style="display:inline-block;width:10px;height:10px;` +
      `border-radius:50%;margin-right:8px;vertical-align:middle;` +
      `background-color:${color}"></span>${display}`
    );
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
